type PerfPayload = Record<string, unknown>;

type PendingRoutePerf = {
  path: string;
  source: string;
  responseEndedAtMs: number;
  requestDurationMs: number;
  serverTiming?: Record<string, number>;
  payloadBytes?: number;
};

const PERF_QUERY_KEY = 'perf_debug';
const PERF_STORAGE_KEY = 'iv_perf_debug';
const PENDING_ROUTE_STORAGE_KEY = 'iv_perf_pending_route';

const toRounded = (value: number): number => Math.round(value * 100) / 100;

export const roundPerfMs = (value: number): number => toRounded(value);

export const isPerfDiagnosticsEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return import.meta.env.VITE_PERF_DEBUG === '1';
  }

  const params = new URLSearchParams(window.location.search);
  const queryValue = params.get(PERF_QUERY_KEY);
  if (queryValue === '1') {
    window.localStorage.setItem(PERF_STORAGE_KEY, '1');
  } else if (queryValue === '0') {
    window.localStorage.removeItem(PERF_STORAGE_KEY);
  }

  if (import.meta.env.VITE_PERF_DEBUG === '1') return true;
  return window.localStorage.getItem(PERF_STORAGE_KEY) === '1';
};

export const logPerfEvent = (name: string, payload: PerfPayload = {}): void => {
  if (!isPerfDiagnosticsEnabled()) return;
  console.info(`[IV_PERF] ${name}`, payload);
};

export const getPerfRequestHeaders = (): Record<string, string> | undefined => {
  if (!isPerfDiagnosticsEnabled()) return undefined;
  return {
    'x-sim-perf': '1',
  };
};

export const parseServerTimingHeader = (value: string | null | undefined): Record<string, number> => {
  if (!value) return {};
  return value.split(',').reduce<Record<string, number>>((acc, metric) => {
    const [namePart, ...attributes] = metric.trim().split(';');
    const name = namePart?.trim();
    if (!name) return acc;
    const durAttribute = attributes.find((attribute) => attribute.trim().startsWith('dur='));
    if (!durAttribute) return acc;
    const duration = Number.parseFloat(durAttribute.trim().replace('dur=', ''));
    if (Number.isFinite(duration)) {
      acc[name] = toRounded(duration);
    }
    return acc;
  }, {});
};

export const measurePayloadBytes = (value: unknown): number => {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return 0;
  }
};

export const storePendingRoutePerf = (payload: PendingRoutePerf): void => {
  if (!isPerfDiagnosticsEnabled() || typeof window === 'undefined') return;
  window.sessionStorage.setItem(PENDING_ROUTE_STORAGE_KEY, JSON.stringify(payload));
};

export const consumePendingRoutePerf = (path: string): PendingRoutePerf | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(PENDING_ROUTE_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PendingRoutePerf;
    if (parsed.path !== path) return null;
    window.sessionStorage.removeItem(PENDING_ROUTE_STORAGE_KEY);
    return parsed;
  } catch {
    window.sessionStorage.removeItem(PENDING_ROUTE_STORAGE_KEY);
    return null;
  }
};

